import { useCallback, useMemo } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';

import { useSetCurrentUserPhoneNumber, useSetMarketingCommsConsent } from '#src/app/shared/hooks/session';
import { isUserPhoneNumberValid } from '#src/app/shared/utils/phoneCollection';
import type { PhoneNumber } from '#src/app/shared/utils/phoneCollection';

const PHONE_CONSENT_NAME = 'phone_consent_starter_phone_collection' as const;
const SMS_CONSENT_NAME = 'starter_sms_petition_updates_consent' as const;

/**
 * Identifies which screen is asking for consent, not which backend consent name is recorded —
 * `phone_collection` and `phone_journalist` both save under PHONE_CONSENT_NAME, but show different
 * on-screen disclosure copy, and the recorded consent text must match what the user actually saw.
 */
export type ConsentType = 'phone_collection' | 'phone_journalist' | 'sms';

export type UseConsentReturn = Readonly<{
	/** Records consent for a valid phone number, writing the number first when it changed. */
	saveConsent: (phoneNumberInput: PhoneNumber, userPhoneNumber: PhoneNumber) => Promise<boolean>;
	/**
	 * Records an explicit refusal (`consentGiven: false`) without touching the phone number. A
	 * refusal is a preference in its own right, so it is persisted independently of any number.
	 */
	saveDecline: () => Promise<boolean>;
}>;

function resolveConsentName(type: ConsentType): string {
	return type === 'sms' ? SMS_CONSENT_NAME : PHONE_CONSENT_NAME;
}

/**
 * The @change-corgi/no-invalid-translation-keys lint rule requires translate() call sites to use
 * a string-literal key, so this has to be a switch with an inline literal per case — a lookup
 * table storing the key as data wouldn't satisfy it.
 *
 * Changing a key here changes the text recorded against a consent, which must remain the text the
 * user actually saw on screen.
 */
function resolveConsentText(type: ConsentType, translate: ReturnType<typeof useI18n>['translate']): string {
	switch (type) {
		case 'phone_collection':
			return translate('corgi.petition.starter_onboarding.phone_collection_step.opt_out');
		case 'phone_journalist':
			return translate('corgi.petition.starter_onboarding.phone_collection_step_journalist.consent_disclosure');
		case 'sms':
			return translate('corgi.petition.starter_onboarding.phone_collection_step_sms.sms_disclosure');
		default:
			throw new Error(`Unknown consent type: ${type as string}`);
	}
}

export function useConsent(type: ConsentType): UseConsentReturn {
	const { translate } = useI18n();
	const savePhoneNumberAndConsent = useSetCurrentUserPhoneNumber();
	const setConsent = useSetMarketingCommsConsent();

	const consentText = useMemo(() => resolveConsentText(type, translate), [type, translate]);
	const consentName = useMemo(() => resolveConsentName(type), [type]);

	const saveConsent = useCallback(
		async (phoneNumberInput: PhoneNumber, userPhoneNumber: PhoneNumber): Promise<boolean> => {
			// bail out early if we don't have a valid phone number input
			if (!isUserPhoneNumberValid(phoneNumberInput)) return false;

			const consentResponse = { text: consentText, consentGiven: true, name: consentName };

			// only save consent if the input matches the users phone number
			if (phoneNumberInput === userPhoneNumber) {
				const result = await setConsent(consentResponse);

				if (!result) {
					throw new Error('error saving consent');
				}

				return true;
			}

			// otherwise update the users phone number and save consent
			// the first check in the function ensures we have a valid phone number
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			const result = await savePhoneNumberAndConsent(phoneNumberInput!, consentResponse);

			if (!result) {
				throw new Error('error saving phone number and consent');
			}

			return true;
		},
		[consentText, consentName, setConsent, savePhoneNumberAndConsent],
	);

	const saveDecline = useCallback(async (): Promise<boolean> => {
		const result = await setConsent({ text: consentText, consentGiven: false, name: consentName });

		if (!result) {
			throw new Error('error saving consent refusal');
		}

		return true;
	}, [consentText, consentName, setConsent]);

	return useMemo(() => ({ saveConsent, saveDecline }), [saveConsent, saveDecline]);
}
